import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ExecutiveExportButton from "../components/ExecutiveExportButton";
import { useApi, type ApiError } from "../lib/api";
import { useTenant } from "../lib/tenant";
import type { components } from "../lib/api-types";

type Briefing = components["schemas"]["Briefing"];
type BriefingVerification = components["schemas"]["BriefingVerificationReport"];
type BriefingCitation = components["schemas"]["BriefingCitation"];

type DataTrustBlockDetail = {
  code?: string;
  surface?: string;
  tenant_id?: string;
  trust_gate_status?: string;
  quality_threshold?: number;
  next_actions?: string[];
};

const TYPE_LABEL: Record<Briefing["type"], string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  ad_hoc: "Ad hoc",
};

const AUDIENCE_LABEL: Record<Briefing["audience"], string> = {
  cfo: "CFO",
  ceo: "CEO",
  coo: "COO",
  controller: "Controlling",
  all: "Board",
};

function clsStatus(status?: string | null): string {
  if (status === "pass" || status === "ready" || status === "delivered") return "good";
  if (status === "warning" || status === "generating" || status === "queued") return "warn";
  if (status === "fail" || status === "failed") return "bad";
  return "muted";
}

function asDate(value?: string | null): string {
  if (!value) return "nicht datiert";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE");
}

function fmtEur(value?: number | null): string {
  if (typeof value !== "number") return "offen";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

function fmtInput(value: unknown): string {
  if (typeof value === "number") return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(value);
  if (typeof value === "boolean") return value ? "ja" : "nein";
  if (typeof value === "string") return value;
  if (value === null || typeof value === "undefined") return "offen";
  return JSON.stringify(value);
}

function SafeRef({ value, label }: { value: string; label?: string }) {
  const text = label || value;
  if (value.startsWith("/app/")) return <Link to={value}>{text}</Link>;
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return <a href={value} target="_blank" rel="noreferrer">{text}</a>;
  }
  return <code>{value}</code>;
}

function CitationLink({ citation }: { citation: BriefingCitation }) {
  if (citation.route) {
    return (
      <SafeRef value={citation.route} label={citation.label} />
    );
  }
  return <strong>{citation.label}</strong>;
}

function Empty({ children }: { children: string }) {
  return <p className="muted small">{children}</p>;
}

function extractDataTrustBlock(error: unknown): DataTrustBlockDetail | null {
  const apiError = error as ApiError | undefined;
  const details = apiError?.details;
  const candidate = details && typeof details === "object" && "detail" in details
    ? (details as { detail?: unknown }).detail
    : details;
  if (!candidate || typeof candidate !== "object") return null;
  const detail = candidate as DataTrustBlockDetail;
  return detail.code === "data_trust_gate_blocked" ? detail : null;
}

function DataTrustBlocked({ detail }: { detail: DataTrustBlockDetail }) {
  return (
    <div className="lineage-card briefing-verification fail">
      <h1>Data Trust Gate blockiert dieses Briefing</h1>
      <p className="muted">
        Tenant {detail.tenant_id ?? "unbekannt"} · Status {detail.trust_gate_status ?? "blocked"}
        {typeof detail.quality_threshold === "number" && <> · Schwelle {detail.quality_threshold}/100</>}
      </p>
      {(detail.next_actions ?? []).length > 0 && (
        <ul className="lineage-list">
          {(detail.next_actions ?? []).slice(0, 4).map((action) => (
            <li key={action}>
              <span className="pill warn">next</span>
              <span>{action}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="brief-cta">
        <Link to="/app/sources" className="btn ghost">Quellen prüfen</Link>
        <Link to="/app/command-center" className="btn ghost">Command Center</Link>
      </div>
    </div>
  );
}

export default function BriefingDetailPage() {
  const { briefingId } = useParams<{ briefingId: string }>();
  const api = useApi();
  const { effectiveTenantId } = useTenant();

  const detail = useQuery({
    queryKey: ["briefing-detail", effectiveTenantId, briefingId],
    queryFn: () => api.get<Briefing>(`/briefings/${briefingId}`),
    enabled: !!briefingId,
  });

  const verification = useQuery({
    queryKey: ["briefing-detail", effectiveTenantId, briefingId, "verification"],
    queryFn: () => api.get<BriefingVerification>(`/briefings/${briefingId}/verification`),
    enabled: !!briefingId,
  });

  if (detail.isLoading) {
    return <section className="brief-page"><p className="muted">Lade Briefing ...</p></section>;
  }

  const detailBlock = extractDataTrustBlock(detail.error);
  if (detail.isError) {
    return (
      <section className="brief-page">
        <Link to="/app/briefings" className="btn ghost">Zur Briefing-Liste</Link>
        {detailBlock ? (
          <DataTrustBlocked detail={detailBlock} />
        ) : (
          <div className="lineage-card briefing-verification fail">
            <h1>Briefing nicht verfügbar</h1>
            <p className="muted">Der tenant-gescoped Briefing-Datensatz wurde nicht gefunden oder das Data Trust Gate blockiert die Ausgabe.</p>
          </div>
        )}
      </section>
    );
  }

  const briefing = detail.data;
  if (!briefing) {
    return <section className="brief-page"><p>Briefing nicht gefunden.</p></section>;
  }

  const verify = verification.data;
  const roi = briefing.roi_context;
  const inputRows = roi?.inputs ? Object.entries(roi.inputs).slice(0, 8) : [];
  const citations = briefing.citations ?? [];

  return (
    <section className="brief-page briefing-detail-page">
      <header className="brief-hero briefing-detail-hero">
        <div>
          <p className="hero-eyebrow">Executive Briefing</p>
          <h1>{TYPE_LABEL[briefing.type]} Briefing für {AUDIENCE_LABEL[briefing.audience]}</h1>
          <p className="muted">
            {briefing.id} · {asDate(briefing.created_at)} · {briefing.status}
          </p>
        </div>
        <div className="brief-export-row">
          <Link to="/app/briefings" className="btn ghost">Liste</Link>
          <ExecutiveExportButton
            type="evidence_package"
            format="pdf"
            audience={briefing.audience}
            briefingId={briefing.id}
            scope={["briefing-detail", `briefing:${briefing.id}`]}
            label="Evidence Pack exportieren"
            compact
          />
        </div>
      </header>

      {roi && (
        <section className="lineage-card roi-context-card">
          <div className="lineage-head">
            <div>
              <h2>ROI Board Context</h2>
              <span className="muted small">Monte-Carlo, Replay-Contract und Board-Empfehlung</span>
            </div>
            <span className={`pill ${clsStatus(roi.contract_status)}`}>{roi.contract_status ?? "unknown"}</span>
          </div>
          <div className="stat-grid compact">
            <div>
              <span>Simulation</span>
              <strong>{roi.simulation_id ?? "offen"}</strong>
            </div>
            <div>
              <span>ROI Run</span>
              <strong>{roi.run_id ?? "offen"}</strong>
            </div>
            <div>
              <span>Decision State</span>
              <strong>{roi.decision_state ?? "offen"}</strong>
            </div>
            <div>
              <span>Model</span>
              <strong>{briefing.model_route ?? "local"}</strong>
            </div>
          </div>
          {roi.recommendation && <p className="brief-context-recommendation">{roi.recommendation}</p>}
          {inputRows.length > 0 && (
            <dl className="brief-input-grid">
              {inputRows.map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{fmtInput(value)}</dd>
                </div>
              ))}
            </dl>
          )}
          <div className="brief-cta">
            {roi.simulation_id && <Link to={`/app/roi?simulation=${encodeURIComponent(roi.simulation_id)}`} className="btn ghost">Simulation öffnen</Link>}
            {roi.run_id && <Link to={`/app/agents?run=${encodeURIComponent(roi.run_id)}`} className="btn ghost">ROI Run prüfen</Link>}
          </div>
        </section>
      )}

      {verify && (
        <section className={`lineage-card briefing-verification ${verify.status}`}>
          <div className="lineage-head">
            <div>
              <h2>Citation Gate</h2>
              <span className="muted small">
                {verify.resolved_citation_count}/{verify.citation_count} Citations · {verify.audit_namespace}
              </span>
            </div>
            <span className={`pill ${clsStatus(verify.status)}`}>{verify.status}</span>
          </div>
          <div className="stat-grid compact">
            <div><span>Local Model</span><strong>{verify.local_model ? "ja" : "nein"}</strong></div>
            <div><span>Missing</span><strong>{verify.missing_citation_count}</strong></div>
            <div><span>Evidence</span><strong>{verify.evidence_id ?? "none"}</strong></div>
            <div><span>Run</span><strong>{verify.run_id ?? "none"}</strong></div>
          </div>
          <ul className="lineage-list">
            {(verify.checks ?? []).map((check) => (
              <li key={check.id}>
                <strong>{check.title}</strong>
                <span className={`pill ${clsStatus(check.status)}`}>{check.status}</span>
                <p>{check.detail}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <article className="briefing-five">
        <header className="briefing-head">
          <div>
            <h2>Entscheidungslage</h2>
            <p className="muted small">
              {briefing.model_route ?? "local"} · {briefing.prompt_version ?? "prompt-version offen"} · {briefing.evalset_id ?? "evalset offen"}
            </p>
          </div>
          <span className={`pill ${clsStatus(briefing.status)}`}>{briefing.status}</span>
        </header>

        <div className="five-cols">
          <div className="col">
            <div className="col-title">Was ist passiert?</div>
            {(briefing.highlights ?? []).length === 0 && <Empty>Keine KPI-Bewegungen erfasst.</Empty>}
            <ul className="col-list">
              {(briefing.highlights ?? []).map((item, index) => (
                <li key={index}>
                  <strong>{item.metric}</strong>
                  {typeof item.delta_pct === "number" && (
                    <span className={`pill ${item.direction === "up" ? "good" : item.direction === "down" ? "bad" : "muted"}`}>
                      {item.delta_pct > 0 ? "+" : ""}{item.delta_pct.toFixed(1)}%
                    </span>
                  )}
                  <p>{item.summary}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="col">
            <div className="col-title">Warum?</div>
            {(briefing.rca ?? []).length === 0 && <Empty>Keine Root-Cause-Hypothesen erfasst.</Empty>}
            <ul className="col-list">
              {(briefing.rca ?? []).map((item, index) => (
                <li key={index}>
                  <strong>{Math.round(item.confidence * 100)}% Confidence</strong>
                  <p>{item.hypothesis}</p>
                  {item.evidence_ref && <SafeRef value={item.evidence_ref} />}
                </li>
              ))}
            </ul>
          </div>

          <div className="col">
            <div className="col-title">Was tun?</div>
            {(briefing.actions ?? []).length === 0 && <Empty>Keine Maßnahmen erfasst.</Empty>}
            <ul className="col-list">
              {(briefing.actions ?? []).map((item, index) => (
                <li key={index}>
                  <strong>{item.title}</strong>
                  <span className="muted small">{fmtEur(item.impact_eur)} · {item.due ?? "Termin offen"}</span>
                  {item.owner && <p>Owner: {item.owner}</p>}
                </li>
              ))}
            </ul>
          </div>

          <div className="col">
            <div className="col-title">Wer owns it?</div>
            {briefing.owner ? (
              <div className="owner-card">
                <strong>{briefing.owner.name}</strong>
                <span>{briefing.owner.role}</span>
                {briefing.owner.escalation && <p>{briefing.owner.escalation}</p>}
              </div>
            ) : (
              <Empty>Kein Owner gesetzt.</Empty>
            )}
          </div>

          <div className="col">
            <div className="col-title">Welche Evidenz?</div>
            <ul className="col-list evidence-list">
              {briefing.evidence_id && (
                <li>
                  <Link to={`/app/agents?evidence=${encodeURIComponent(briefing.evidence_id)}`}>Evidence prüfen</Link>
                  <code>{briefing.evidence_id}</code>
                </li>
              )}
              {briefing.run_id && (
                <li>
                  <Link to={`/app/agents?run=${encodeURIComponent(briefing.run_id)}`}>Agent Run</Link>
                  <code>{briefing.run_id}</code>
                </li>
              )}
              {briefing.evidence_bundle && (
                <li>
                  <strong>Evidence Bundle</strong>
                  <SafeRef value={briefing.evidence_bundle} />
                </li>
              )}
              {briefing.langfuse_trace && (
                <li>
                  <strong>Trace</strong>
                  <SafeRef value={briefing.langfuse_trace} />
                </li>
              )}
              {citations.map((citation, index) => (
                <li key={`${citation.ref}-${index}`}>
                  <CitationLink citation={citation} />
                  <span className="muted small">{citation.evidence_id ?? "no-evidence"}</span>
                  <code>{citation.ref}</code>
                </li>
              ))}
              {!briefing.evidence_id && !briefing.run_id && !briefing.evidence_bundle && !citations.length && (
                <li className="muted small">Keine verlinkten Artefakte.</li>
              )}
            </ul>
          </div>
        </div>
      </article>
    </section>
  );
}
